import { requestJson } from "../../lib/api";
import type {
  BusinessAssessmentRecord,
  ConsultingRecommendationRecord,
  ConsultingOpportunityRecord,
  ConsultingProjectRecord,
  ImplementationRoadmapRecord,
  TruevanceServiceLibraryItem,
  TruevanceStore,
  TruevanceWorkspacePayload,
} from "./TruevanceConnectData";

export async function loadTruevanceWorkspace() {
  return requestJson<TruevanceWorkspacePayload>("/api/admin/truevance");
}

export async function loadTruevanceServiceLibrary() {
  return requestJson<{ items: TruevanceServiceLibraryItem[] }>("/api/admin/truevance/service-library");
}

export async function createTruevanceOpportunity(payload: Partial<ConsultingOpportunityRecord>) {
  return requestJson<{ opportunity: ConsultingOpportunityRecord }>("/api/admin/truevance/opportunities", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function updateTruevanceOpportunity(opportunityId: string, payload: Partial<ConsultingOpportunityRecord>) {
  return requestJson<{ opportunity: ConsultingOpportunityRecord }>(`/api/admin/truevance/opportunities/${opportunityId}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export async function appendTruevanceOpportunityEvent(opportunityId: string, payload: { title: string; detail?: string; relatedLink?: string }) {
  return requestJson<{ opportunity: ConsultingOpportunityRecord }>(`/api/admin/truevance/opportunities/${opportunityId}`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function archiveTruevanceOpportunity(opportunityId: string) {
  return requestJson<{ opportunity: ConsultingOpportunityRecord }>(`/api/admin/truevance/opportunities/${opportunityId}`, {
    method: "DELETE",
  });
}

export async function createTruevanceAssessment(payload: Partial<BusinessAssessmentRecord> & { assessmentType?: string }) {
  return requestJson<{ assessment: BusinessAssessmentRecord }>("/api/admin/truevance/assessments", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function updateTruevanceAssessment(assessmentId: string, payload: Partial<BusinessAssessmentRecord>) {
  return requestJson<{ assessment: BusinessAssessmentRecord }>(`/api/admin/truevance/assessments/${assessmentId}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export async function appendTruevanceAssessmentEvent(assessmentId: string, payload: { title: string; detail?: string; relatedLink?: string }) {
  return requestJson<{ assessment: BusinessAssessmentRecord }>(`/api/admin/truevance/assessments/${assessmentId}`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function archiveTruevanceAssessment(assessmentId: string) {
  return requestJson<{ assessment: BusinessAssessmentRecord }>(`/api/admin/truevance/assessments/${assessmentId}`, {
    method: "DELETE",
  });
}

export async function createTruevanceRecommendation(payload: Partial<ConsultingRecommendationRecord>) {
  return requestJson<{ recommendation: ConsultingRecommendationRecord }>("/api/admin/truevance/recommendations", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function updateTruevanceRecommendation(recommendationId: string, payload: Partial<ConsultingRecommendationRecord>) {
  return requestJson<{ recommendation: ConsultingRecommendationRecord }>(`/api/admin/truevance/recommendations/${recommendationId}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export async function appendTruevanceRecommendationEvent(recommendationId: string, payload: { title: string; detail?: string; relatedLink?: string }) {
  return requestJson<{ recommendation: ConsultingRecommendationRecord }>(`/api/admin/truevance/recommendations/${recommendationId}`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function archiveTruevanceRecommendation(recommendationId: string) {
  return requestJson<{ recommendation: ConsultingRecommendationRecord }>(`/api/admin/truevance/recommendations/${recommendationId}`, {
    method: "DELETE",
  });
}

export async function createTruevanceRoadmap(payload: Partial<ImplementationRoadmapRecord>) {
  return requestJson<{ roadmap: ImplementationRoadmapRecord }>("/api/admin/truevance/roadmaps", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function updateTruevanceRoadmap(roadmapId: string, payload: Partial<ImplementationRoadmapRecord>) {
  return requestJson<{ roadmap: ImplementationRoadmapRecord }>(`/api/admin/truevance/roadmaps/${roadmapId}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export async function appendTruevanceRoadmapEvent(roadmapId: string, payload: { title: string; detail?: string; relatedLink?: string }) {
  return requestJson<{ roadmap: ImplementationRoadmapRecord }>(`/api/admin/truevance/roadmaps/${roadmapId}`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function archiveTruevanceRoadmap(roadmapId: string) {
  return requestJson<{ roadmap: ImplementationRoadmapRecord }>(`/api/admin/truevance/roadmaps/${roadmapId}`, {
    method: "DELETE",
  });
}

export async function createTruevanceProject(payload: Partial<ConsultingProjectRecord> & { projectType?: string }) {
  return requestJson<{ project: ConsultingProjectRecord }>("/api/admin/truevance/projects", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function updateTruevanceProject(projectId: string, payload: Partial<ConsultingProjectRecord>) {
  return requestJson<{ project: ConsultingProjectRecord }>(`/api/admin/truevance/projects/${projectId}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export async function appendTruevanceProjectEvent(projectId: string, payload: { title: string; detail?: string; relatedLink?: string }) {
  return requestJson<{ project: ConsultingProjectRecord }>(`/api/admin/truevance/projects/${projectId}`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function archiveTruevanceProject(projectId: string) {
  return requestJson<{ project: ConsultingProjectRecord }>(`/api/admin/truevance/projects/${projectId}`, {
    method: "DELETE",
  });
}

export function calculateTruevanceMetrics(store: TruevanceStore) {
  const openOpportunities = store.opportunities.filter((item) => item.status === "Active").length;
  const activeAssessments = store.assessments.filter((item) => item.status === "In Progress").length;
  const activeProjects = store.projects.filter((item) => item.status === "Active").length;
  const consultingRevenue = store.projects.reduce((sum, item) => sum + item.revenue, 0);
  const pipelineValue = store.opportunities.reduce((sum, item) => sum + item.estimatedRevenue * (item.probability / 100), 0);
  const closedOpportunities = store.opportunities.filter((item) => item.status === "Won" || item.status === "Lost").length;
  const wonOpportunities = store.opportunities.filter((item) => item.status === "Won").length;
  const winRate = closedOpportunities ? Math.round((wonOpportunities / closedOpportunities) * 100) : 0;
  const averageProjectValue = store.projects.length ? Math.round(consultingRevenue / store.projects.length) : 0;
  const projectCompletionRate = store.projects.length
    ? Math.round(store.projects.reduce((sum, item) => sum + item.completionPercent, 0) / store.projects.length)
    : 0;

  return {
    openOpportunities,
    activeAssessments,
    activeProjects,
    consultingRevenue,
    pipelineValue,
    winRate,
    averageProjectValue,
    projectCompletionRate,
  };
}
